import { faCopy, faExternalLink } from '@fortawesome/pro-solid-svg-icons';
import React, { useState } from 'react';
import { Text } from '../../../components/base/Text';
import { Button } from '../../../components/forms/Button';
import { Modal } from '../../../components/overlays/Modal';
import { useThemeColors } from '../../../providers/ThemeProvider';
import { cn } from '../../../utils/cn';

interface TextExpansionModalProps {
	isOpen: boolean;
	onClose: () => void;
	title?: string;
	text: string;
	linkUrl?: string;
	isCodeBlock?: boolean;
}

export const TextExpansionModal: React.FC<TextExpansionModalProps> = ({
	isOpen,
	onClose,
	title = 'Full Text',
	text,
	linkUrl,
	isCodeBlock = false,
}) => {
	const colors = useThemeColors();
	const [copySuccess, setCopySuccess] = useState(false);

	const handleCopyToClipboard = async () => {
		try {
			await navigator.clipboard.writeText(text);
			setCopySuccess(true);
			setTimeout(() => setCopySuccess(false), 2000);
		} catch (err) {
			// Error: Failed to copy text:
		}
	};

	const handleOpenLink = () => {
		if (linkUrl) {
			window.open(linkUrl, '_blank');
		}
	};

	return (
		<Modal isOpen={isOpen} onClose={onClose} title={title} size="lg">
			<div className="flex flex-col h-full">
				<div className="flex-1 max-h-[400px] overflow-y-auto">
					<div className="p-4 min-h-[200px]">
						{isCodeBlock ? (
							<pre
								className="text-xs leading-relaxed select-text overflow-x-auto p-3 rounded"
								style={{
									fontFamily:
										'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
									backgroundColor: colors.values.backgroundSecondary,
									color: colors.values.textPrimary,
								}}
							>
								<code>{text}</code>
							</pre>
						) : (
							<Text
								className="text-sm leading-relaxed whitespace-pre-wrap select-text"
								style={{
									fontFamily: 'system-ui, -apple-system, sans-serif',
								}}
							>
								{text}
							</Text>
						)}
					</div>
				</div>

				{/* Actions */}
				<div
					className={cn('flex justify-end gap-2 pt-4 mt-4 border-t')}
					style={{ borderColor: colors.values.borderDefault }}
				>
					{/* Copy to Clipboard Button */}
					<Button
						variant="outline"
						icon={faCopy}
						onClick={handleCopyToClipboard}
						disabled={copySuccess}
					>
						{copySuccess ? 'Copied!' : 'Copy Text'}
					</Button>

					{/* Open Link Button */}
					{linkUrl && (
						<Button
							variant="outline"
							icon={faExternalLink}
							onClick={handleOpenLink}
						>
							Open Link
						</Button>
					)}

					{/* Close Button */}
					<Button variant="primary" onClick={onClose}>
						Close
					</Button>
				</div>
			</div>
		</Modal>
	);
};
